import { TabList, TabPanel } from '@mui/lab'
import { Box, IconButton, Paper, Stack, styled, Tab, Toolbar } from '@mui/material'
import { useAppSelector } from '~/app/hooks'
import { currentTheme } from '~/redux/features'
import classNames from 'classnames/bind'
import styles from './StyledComponent.module.scss'

const cx = classNames.bind(styles)

interface EFTabListWrapperProps {
  isNested?: boolean
}
interface EFTabListProps {
  isNested?: boolean
}

export const EFTabList = styled(TabList)<EFTabListProps>(({ theme, isNested }) => ({
  '& .MuiTabs-indicator': { backgroundColor: 'var(--active-color)' },
  '& .MuiTab-root': { padding: '1rem 1.5rem' },
  '& .MuiTab-root.Mui-selected': {
    color: 'var(--active-color)'
  },
  borderColor: theme.palette.mode === 'dark' ? 'var(--border-dark-color) !important' : '',
  ...(isNested && {
    width: 'auto',
    height: '100%',
    borderRight: '1px solid #E6E9EE',
    '& .MuiTabs-indicator': { left: 0, backgroundColor: 'var(--active-color)' },
    '& .MuiTab-root': { padding: '1rem 2rem' },
    '& .MuiTab-root.Mui-selected': {
      background: theme.palette.mode === 'dark' ? 'var(--actived-dark)' : '#E6F8FC',
      color: 'var(--active-color)'
    }
  }),
  [theme.breakpoints.down(800)]: {
    width: '100%',
    '& .MuiTabs-indicator': {
      backgroundColor: 'var(--active-color)'
    },
    '& .MuiTab-root': {
      border: isNested ? 'none' : ''
    },
    '& .MuiTab-root.Mui-selected': {
      background: 'transparent !important',
      color: 'var(--active-color)'
    }
  }
}))

export const EFTab = styled(Tab)(({ theme }) => ({
  textTransform: 'capitalize',
  width: 'auto',
  alignItems: 'flex-start',
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? 'white' : 'var(--dark)',
  [theme.breakpoints.down(800)]: {
    width: 'calc(100%/3)',
    alignItems: 'center',
    justifyContent: 'center !important'
  }
}))

export const EFTabPanel = styled(TabPanel)(({ theme }) => ({
  overflow: 'auto',
  width: '85%',
  [theme.breakpoints.down(800)]: {
    width: '100%',
    paddingLeft: '0 !important',
    paddingRight: '0 !important'
  }
}))

export const EFTabListWrapper = styled(Box)<EFTabListWrapperProps>(
  ({ theme, isNested }) => ({
    boxShadow:
      theme.palette.mode === 'dark'
        ? 'inset 1px -4px 1px -2px #4d4d4d'
        : 'inset 1px -4px 2px -2px #E6E9EE',
    width: isNested ? '15%' : '100%',
    [theme.breakpoints.down(800)]: {
      boxShadow: 'none',
      borderBottom: '1px solid #56565630',
      width: '100%'
    }
  })
)

export const EFToolbar = styled(Toolbar)(() => ({
  display: 'flex',
  flexDirection: 'column',
  paddingRight: '0 !important',
  paddingLeft: '0 !important',
  minHeight: 'auto !important'
}))

export const EFPageContainer = styled(Stack)(() => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
}))

export const EFIconButton = styled(IconButton)(({ theme }) => ({
  height: '2.5rem',
  width: '2.5rem',
  '& > svg': {
    color: theme.palette.mode === 'dark' ? 'color: white !important' : '',
    height: '24px !important',
    width: '24px !important'
  }
}))

export const EFToolbarContainer = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const theme = useAppSelector(currentTheme)
  return (
    <Paper
      className={cx('toolbar-container', className, theme)}
      sx={{ width: '100%', boxShadow: 'none' }}
    >
      {children}
    </Paper>
  )
}

export const EFTitlebarContainer = ({ children }: { children: React.ReactNode }) => {
  const theme = useAppSelector(currentTheme)
  return <Stack className={cx('title-bar-container', theme)}>{children}</Stack>
}

export const EFPageContent = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const theme = useAppSelector(currentTheme)
  return <Box className={cx('page-content', className, theme)}>{children}</Box>
}

export const EFFooterDialogWrapper = styled(Stack)(({ theme }) => ({
  position: 'sticky',
  flexDirection: 'row',
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '1rem 0',
  background: theme.palette.mode === 'dark' ? 'var(--dark-gray-background)' : 'white'
}))
